/** Teléfonos AR (+54): normalize, display, tel:/wa.me. */

export function digitsOnlyPhone(raw: string): string {
  return raw.replace(/\D/g, '');
}

/** Parte nacional editable cuando el UI muestra prefijo +54. */
export function nationalPartFromStored(raw: string): string {
  const d = digitsOnlyPhone(raw);
  if (d.startsWith('549')) return d.slice(3);
  if (d.startsWith('54')) return d.slice(2);
  return d;
}

/**
 * Teléfono E.164 con +54.
 * Acepta: "11 5555-5555", "011...", "+54 11...", "5411...".
 * Vacío → null (campo opcional).
 */
export function normalizeArPhone(raw: string): string | null {
  let d = digitsOnlyPhone(raw.trim());
  if (!d) return null;
  if (d.startsWith('00')) d = d.slice(2);
  if (d.startsWith('54')) {
    // ok
  } else if (d.startsWith('0')) {
    d = `54${d.slice(1)}`;
  } else {
    d = `54${d}`;
  }
  // Mínimo +54 + área(2) + número(6) ≈ 10 dígitos; máx E.164 15
  if (d.length < 10 || d.length > 15) return null;
  return `+${d}`;
}

/**
 * WhatsApp en dígitos para wa.me (549…).
 * Inserta el 9 de móvil AR si vino como teléfono fijo/local.
 */
export function normalizeArWhatsApp(raw: string): string | null {
  let d = digitsOnlyPhone(raw.trim());
  if (!d) return null;
  if (d.startsWith('00')) d = d.slice(2);
  if (d.startsWith('549')) {
    // ok
  } else if (d.startsWith('54') && d.length >= 10) {
    d = `549${d.slice(2)}`;
  } else if (d.startsWith('9') && d.length >= 11) {
    d = `54${d}`;
  } else if (d.startsWith('0')) {
    d = `549${d.slice(1)}`;
  } else {
    d = `549${d}`;
  }
  // 549 + 10 dígitos típicos (área+número) ≈ 12–13
  if (d.length < 12 || d.length > 15) return null;
  return d;
}

export function isCompleteArPhone(raw: string): boolean {
  if (!raw.trim()) return true;
  return normalizeArPhone(raw) != null;
}

/** Vacío → null; válido → E.164; inválido → null (caller puede mostrar error). */
export function normalizeOptionalArPhone(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim();
  if (!t) return null;
  return normalizeArPhone(t);
}

export function isCompleteArWhatsApp(raw: string): boolean {
  if (!raw.trim()) return true;
  return normalizeArWhatsApp(raw) != null;
}

/** Alias de normalizeArWhatsApp para links. */
export function whatsAppDigits(raw: string): string | null {
  return normalizeArWhatsApp(raw);
}

/** `tel:+54…` o null si no se puede normalizar. */
export function telHref(raw: string | null | undefined): string | null {
  if (raw == null || !String(raw).trim()) return null;
  const n = normalizeArPhone(String(raw));
  if (n) return `tel:${n}`;
  const d = digitsOnlyPhone(String(raw));
  if (d.length >= 8) return `tel:+${d.startsWith('54') ? d : `54${d}`}`;
  return null;
}

/** `https://wa.me/549…` con mensaje opcional. */
export function whatsAppHref(raw: string | null | undefined, message?: string): string | null {
  if (raw == null || !String(raw).trim()) return null;
  const digits = normalizeArWhatsApp(String(raw));
  if (!digits) return null;
  if (message != null && message.length > 0) {
    return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
  }
  return `https://wa.me/${digits}`;
}

/**
 * Display legible AR (alineado a soporte: +54 9 11 7619-6898).
 * Inválido → trim original; vacío → ''.
 */
export function formatArPhoneDisplay(raw: string | null | undefined): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';

  let d = digitsOnlyPhone(trimmed);
  if (!d) return trimmed;
  if (d.startsWith('00')) d = d.slice(2);

  if (!d.startsWith('54')) {
    if (d.startsWith('0')) d = `54${d.slice(1)}`;
    else d = `54${d}`;
  }

  if (d.length < 10 || d.length > 15) return trimmed;

  let rest = d.slice(2);
  let mobileNine = false;
  if (rest.startsWith('9') && rest.length >= 11) {
    mobileNine = true;
    rest = rest.slice(1);
  }

  let area: string;
  let subscriber: string;
  if (rest.startsWith('11') && rest.length >= 10) {
    area = '11';
    subscriber = rest.slice(2);
  } else if (rest.length === 10) {
    area = rest.slice(0, 3);
    subscriber = rest.slice(3);
  } else if (rest.length === 9) {
    area = rest.slice(0, 3);
    subscriber = rest.slice(3);
  } else if (rest.length > 10) {
    area = rest.slice(0, 4);
    subscriber = rest.slice(4);
  } else if (rest.length >= 8) {
    area = rest.slice(0, 2);
    subscriber = rest.slice(2);
  } else {
    return trimmed;
  }

  if (!subscriber) return trimmed;
  const formattedSub =
    subscriber.length > 4 ? `${subscriber.slice(0, -4)}-${subscriber.slice(-4)}` : subscriber;

  return mobileNine ? `+54 9 ${area} ${formattedSub}` : `+54 ${area} ${formattedSub}`;
}

/** Aplica normalización al draft de contacto tienda antes de guardar. */
export function normalizeStoreContactFields(
  phone: string,
  whatsapp: string,
): {
  phone: string;
  whatsapp: string;
  phoneError?: string;
  whatsappError?: string;
} {
  const phoneTrim = phone.trim();
  const waTrim = whatsapp.trim();
  let phoneOut = '';
  let whatsappOut = '';
  let phoneError: string | undefined;
  let whatsappError: string | undefined;

  if (phoneTrim) {
    const n = normalizeArPhone(phoneTrim);
    if (!n) phoneError = 'Revisá el teléfono (incluí área, ej. 11 5555-5555).';
    else phoneOut = n;
  }
  if (waTrim) {
    const n = normalizeArWhatsApp(waTrim);
    if (!n) whatsappError = 'Revisá el WhatsApp (incluí área, ej. 11 5555-5555).';
    else whatsappOut = n;
  }

  return { phone: phoneOut, whatsapp: whatsappOut, phoneError, whatsappError };
}
